import { useState } from "react";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import { Play, Loader2, Terminal as TermIcon, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { bitEvent } from "@/lib/bit";

const MONACO_LANG = { python: "python", cpp: "cpp", java: "java" };
const LABELS = { python: "Python", cpp: "C++", java: "Java" };

export default function ChallengeRunner({ language, starterCode, submitUrl, onResult }) {
  const [code, setCode] = useState(starterCode);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setCode(starterCode);
    setResult(null);
  };

  const submit = async () => {
    setSubmitting(true);
    bitEvent("task-checking", { task: "Coding-Challenge", code, language });
    try {
      const { data } = await api.post(submitUrl, { code });
      setResult(data);
      const bitContext = `Challenge in ${LABELS[language] || language}\n\nCode:\n${code}${data.stderr ? `\n\nFehlermeldung:\n${data.stderr}` : ""}`;
      if (data.passed) {
        toast.success(data.xp_awarded > 0 ? `Gelöst! +${data.xp_awarded} XP` : "Gelöst! (keine weitere XP, schon zuvor abgeschlossen)");
        bitEvent("task-solved", {
          task: { instructions: "Alle Testfälle der Challenge erfolgreich bestanden." },
          code,
          language,
          xp: data.xp_awarded,
        });
      } else if (data.compile_error) {
        toast.error("Kompilierfehler – sieh dir die Fehlermeldung unten an.");
        bitEvent("compile-error", { context: bitContext });
      } else {
        toast.error("Noch nicht ganz – schau dir die Testfälle an.");
        bitEvent("fail", { text: "Ein paar Tests sind noch rot. Schau dir an, welche Eingaben scheitern.", context: bitContext });
      }
      onResult?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border-2 border-black nb-shadow overflow-hidden bg-[#0D0D12]">
      <div className="flex items-center justify-between gap-1 px-2 sm:px-3 py-2 bg-[#16161d] border-b-2 border-black">
        <div className="flex items-center gap-1.5 text-[#F8F8F2] shrink-0">
          <TermIcon className="h-4 w-4 text-primary" strokeWidth={2.5} />
          <span className="font-mono text-xs font-bold">{LABELS[language] || language}</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={reset}
            data-testid="challenge-reset"
            className="flex items-center gap-1 text-[#aaa] hover:text-white text-xs font-bold p-1.5 rounded transition-colors"
            title="Zurücksetzen"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="challenge-submit"
            title="Einreichen"
            className="flex items-center gap-1 bg-success text-black text-xs font-bold px-2 sm:px-3 py-1.5 rounded-lg border-2 border-black nb-shadow-sm nb-press disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
            ) : (
              <Play className="h-3.5 w-3.5" strokeWidth={2.5} />
            )}
            <span className="hidden sm:inline">Einreichen</span>
          </button>
        </div>
      </div>

      <Editor
        height="320px"
        language={MONACO_LANG[language] || "plaintext"}
        theme="vs-dark"
        value={code}
        onChange={(v) => setCode(v ?? "")}
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          fontFamily: "'JetBrains Mono', monospace",
          scrollBeyondLastLine: false,
          padding: { top: 12 },
          lineNumbers: "on",
          automaticLayout: true,
          wordWrap: "on",
          wrappingIndent: "same",
        }}
      />

      <div className="bg-[#0D0D12] border-t-2 border-black px-4 py-3 min-h-[80px]">
        <div className="text-[10px] font-mono font-bold text-[#666] mb-1">ERGEBNIS</div>
        {!result && (
          <p className="font-mono text-sm text-[#F8F8F2]">Klicke auf "Einreichen", um deine Lösung zu testen.</p>
        )}
        {result?.compile_error && (
          <pre data-testid="challenge-compile-error" className="font-mono text-sm text-error whitespace-pre-wrap break-words">
            Kompilierfehler:{"\n"}
            {result.stderr}
          </pre>
        )}
        {result && !result.compile_error && (
          <div className="space-y-1.5">
            {result.results.map((r, i) => (
              <div key={i} data-testid={`challenge-result-${i}`} className="flex items-start gap-2 font-mono text-sm">
                {r.passed ? (
                  <CheckCircle2 className="h-4 w-4 text-success shrink-0 mt-0.5" strokeWidth={2.5} />
                ) : (
                  <XCircle className="h-4 w-4 text-error shrink-0 mt-0.5" strokeWidth={2.5} />
                )}
                <div className="text-[#F8F8F2] break-words min-w-0">
                  {r.hidden ? (
                    <span>
                      Versteckter Test #{i + 1} — {r.passed ? "bestanden" : "fehlgeschlagen"}
                    </span>
                  ) : (
                    <span>
                      Test #{i + 1}: Eingabe {JSON.stringify(r.input)} → erwartet {JSON.stringify(r.expected)}
                      {!r.passed && (r.error ? `, Fehler: ${r.error}` : `, erhalten ${JSON.stringify(r.actual)}`)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
